// B-361 · LA IA DEL EQUIPO DE SOPORTE, A MANO EN TODO EL PANEL (28-sep-2026)
//
// El dueño eligió, además de «Redactar con la IA» dentro de cada consulta, «el botón flotante en el panel, para
// preguntarle a la IA cualquier cosa sin abrir una consulta (y copiar la respuesta)». Sabe lo mismo que el asistente de las
// clínicas (sus pantallas y el centro de ayuda que ellas leen) y contesta como lo escribiría una persona del equipo, para
// copiarlo y pegarlo en el chat de la clínica (POST /api/superadmin/soporte/ia/consulta · services/borradorSoporte.consultar).
// No guarda nada: la conversación vive aquí y viaja entera en cada pregunta. Solo se pinta con la sección de soporte.
import { useState, useRef, useEffect } from 'react'
import api from '../services/api'
import { useToast } from '../context/ToastContext'
import { Ico } from './ui'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export default function ConsultaIASoporte() {
  const toast = useToast()
  const [abierto, setAbierto] = useState(false)
  const [mensajes, setMensajes] = useState([])   // [{ de: 'yo' | 'ia', texto, articulos }]
  const [pregunta, setPregunta] = useState('')
  const [pensando, setPensando] = useState(false)
  const finRef = useRef(null)
  useEffect(() => {
    try { if (finRef.current && finRef.current.scrollIntoView) finRef.current.scrollIntoView({ block: 'end' }) } catch (_) { /* sin DOM */ }
  }, [mensajes, pensando])

  const preguntar = async () => {
    const p = pregunta.trim()
    if (!p || pensando) return
    const historial = mensajes.map(m => ({ de: m.de, texto: m.texto }))
    setMensajes(ms => [...ms, { de: 'yo', texto: p }])
    setPregunta('')
    setPensando(true)
    try {
      const { data } = await api.post('/superadmin/soporte/ia/consulta', { pregunta: p, historial })
      setMensajes(ms => [...ms, { de: 'ia', texto: data?.texto || '', articulos: Array.isArray(data?.articulos) ? data.articulos : [] }])
    } catch (e) {
      toast.error(e?.response?.data?.error || t('components.ConsultaIASoporte.no_se_ha_podido_preguntar', 'No se ha podido preguntar a la IA ahora. Vuelve a intentarlo.'))
      // La pregunta no se pierde: vuelve a la caja.
      setMensajes(ms => ms.slice(0, -1))
      setPregunta(p)
    } finally {
      setPensando(false)
    }
  }

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto)
      toast.success(t('components.ConsultaIASoporte.copiado_pegalo_en_el_chat', 'Copiado: pégalo en el chat de la clínica.'))
    } catch (_) {
      toast.error(t('components.ConsultaIASoporte.no_se_ha_podido_copiar', 'No se ha podido copiar: selecciona el texto y cópialo a mano.'))
    }
  }

  const alPulsarTecla = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); preguntar() }
  }

  return (
    <>
      <button type="button" data-boton-consulta-ia data-boton-flotante-ayuda="" onClick={() => setAbierto(a => !a)}
        title={abierto ? t('components.ConsultaIASoporte.cerrar_la_ia_de_soporte', 'Cerrar la IA de soporte') : t('components.ConsultaIASoporte.preguntale_a_la_ia_de', 'Pregúntale a la IA de soporte')} aria-expanded={abierto}
        style={{ position: 'fixed', bottom: 'calc(16px + var(--sa-abajo))', right: '16px', width: '52px', height: '52px', borderRadius: '50%', background: 'var(--primary)', color: 'var(--sobre-primary)', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.22)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Ico n={abierto ? 'x' : 'bot'} s={24} />
      </button>
      {abierto && (
        <section data-consulta-ia aria-label={t('components.ConsultaIASoporte.ia_de_soporte', 'IA de soporte')}
          style={{ position: 'fixed', bottom: 'calc(80px + var(--sa-abajo))', right: '16px', width: 'min(420px, calc((100vw / var(--z, 1)) - 32px))', height: 'min(580px, calc(var(--alto-pantalla) - var(--sa-arriba) - var(--sa-abajo) - 110px))', background: 'var(--bg, var(--surface))', border: '1px solid var(--border)', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.30)', zIndex: 2000, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <header style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>{t('components.ConsultaIASoporte.preguntale_a_la_ia', 'Pregúntale a la IA')}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                {t('components.ConsultaIASoporte.contesta_con_lo_que_dice', 'Contesta con lo que dice el centro de ayuda, escrito como lo mandaría el equipo: cópialo y pégalo en el chat de la clínica.')}
              </div>
            </div>
            {mensajes.length > 0 && (
              <button type="button" data-nueva-consulta onClick={() => { setMensajes([]); setPregunta('') }} disabled={pensando}
                title={t('components.ConsultaIASoporte.empezar_otra_conversacion', 'Empezar otra conversación')}
                style={{ fontSize: '12px', fontWeight: 700, padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                {t('components.ConsultaIASoporte.nueva', 'Nueva')}
              </button>
            )}
          </header>

          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {mensajes.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {t('components.ConsultaIASoporte.por_ejemplo_como_le_explico', 'Por ejemplo: «¿cómo le explico a una clínica cómo se añade un doctor?» o «¿qué hace el botón Copiar al resto del horario?».')}
              </div>
            )}
            {mensajes.map((m, i) => (
              m.de === 'yo'
                ? (
                  <div key={i} data-mensaje-yo style={{ alignSelf: 'flex-end', maxWidth: '85%', background: 'var(--primary)', color: 'var(--sobre-primary)', padding: '8px 11px', borderRadius: '12px 12px 4px 12px', fontSize: '13.5px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {m.texto}
                  </div>
                )
                : (
                  <div key={i} data-mensaje-ia style={{ alignSelf: 'flex-start', maxWidth: '92%', background: 'var(--surface)', border: '1px solid var(--border)', padding: '9px 11px', borderRadius: '12px 12px 12px 4px', fontSize: '13.5px', color: 'var(--text-primary)', display: 'grid', gap: '8px' }}>
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{m.texto}</div>
                    {m.articulos && m.articulos.length > 0 && (
                      <div data-fuentes style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{tx('components.ConsultaIASoporte.basado_en', 'Basado en: {{join}}', { join: m.articulos.join(' · ') })}</div>
                    )}
                    <div>
                      <button type="button" data-copiar onClick={() => copiar(m.texto)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, padding: '5px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg, var(--surface))', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        <Ico n="copy" s={13} />{t('components.ConsultaIASoporte.copiar', ' Copiar')}
                      </button>
                    </div>
                  </div>
                )
            ))}
            {pensando && <div data-pensando style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('components.ConsultaIASoporte.pensando', 'Pensando…')}</div>}
            <div ref={finRef} />
          </div>

          <footer style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <textarea id="consulta-ia-soporte" data-pregunta value={pregunta} onChange={e => setPregunta(e.target.value)} onKeyDown={alPulsarTecla}
              rows={2} maxLength={1200} placeholder={t('components.ConsultaIASoporte.escribe_tu_pregunta_intro_para', 'Escribe tu pregunta (Intro para enviar)')}
              style={{ flex: 1, resize: 'none', fontSize: '13.5px', padding: '8px 10px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg, var(--surface))', color: 'var(--text-primary)', fontFamily: 'inherit' }} />
            <button type="button" data-preguntar onClick={preguntar} disabled={pensando || !pregunta.trim()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 12px', borderRadius: '10px', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontWeight: 700, fontSize: '13px', cursor: pensando || !pregunta.trim() ? 'not-allowed' : 'pointer', opacity: pensando || !pregunta.trim() ? 0.6 : 1 }}>
              <Ico n="send" s={14} />{t('components.ConsultaIASoporte.preguntar', ' Preguntar')}
            </button>
          </footer>
        </section>
      )}
    </>
  )
}
